import {
  IResourceComponentsProps,
  useShow,
  useTranslate
} from '@pankod/refine-core'

import { Alert, Col, Icons, Row } from '@pankod/refine-antd'

import { definitions, User } from 'interfaces'
import { useEffect, useState } from 'react'
import { calculateReferralAndWalletAmounts, calculateReferralAndWalletAsync, ORDER_STATUS_ID_SUBSCRIPTION } from 'utility'
import { OrderDetails } from '../../components/orders/details'
import { EstimatedCosts } from '../../components/orders/EstimatedCosts'
import { OrderItems } from '../../components/orders/items'
import { MarketOrders } from '../../components/orders/market_orders'
import { Refunds } from '../../components/orders/refunds'
import { Supplements } from '../../components/orders/supplements'
import { UserAddresses } from '../../components/orders/user_addresses'
import { currentUserMetadata } from 'utility/authLocal'
import { WAREHOUSE_ROLE_ID } from 'utility/constants'
import { WarehouseOrderShow } from '../warehouse/show'

export const OrderShow: React.FC<IResourceComponentsProps> = () => {
  if (Number(currentUserMetadata()?.role_id) === WAREHOUSE_ROLE_ID) return <WarehouseOrderShow />
  return <FullOrderShow />
}

const FullOrderShow: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate()
  const [warningWalletAmountUsed, setWarningWalletAmountUsed] = useState(false)

  const addressFields = 'id, email, name, surnames, phone_number, nif, additional_info, is_company, full_address, postal_code, island_id'
  const { queryResult } = useShow<definitions['Order'] & { user: User }>({
    metaData: {
      select:
        '*, shipments(id, shipped_by, tracking_id), payment_method(id, name), invoices:invoice_id(id, invoice_number, reviewed), user(id, name, email, wallet_balance, created_at, gender, birthday, referred_by_user_id(referral_benefits!referrer_user_id(id, benefit_amount)), referral_benefits!referrer_user_id(status, benefit_amount),' +
        'orders!user(id, total, created_at, status_id, syniva_price, shipping_price, shipping_insurance_price, custom_order_price, wallet_amount_used,' +
        'supplements(supplement, volumetric, deleted), items(shipping_price_to_madrid), marketplace_id(id, shipment_price, free_shipment_price))), administrator(id, name), credit_card(number),' +
        'shipping_method_id, status(id, name), shipping_method(name, shipment_type, shipMode, price, volumetric_factor, price_additional_kg, shipping_method_price_ranges(*)),' +
        `shipping_address(${addressFields}, island(name, boat_extra_cost, plane_extra_cost)), billing_address(${addressFields}),
        subscriptions(id, name),
        supplements(id, status_id, volumetric, billable_volume, supplement, description, deleted, volumetricInfo, parent), estimated_costs(id, status_id), refunds(*), wallet_amount_used,
        items(id, name, url, custom_order_site, shipping_price_to_madrid, price, disabled, syniva_price, iva, quantity, igic_price, igic_percent, tariff_price, delivered_warehouse, has_accessories, returned, seller, cloned_from, reviewed, order_id, deleted),
        marketplace_id(id, shipment_price, free_shipment_price, cif, legal_name, legal_address, province, postal_code, extra_info, is_custom_shop, iva_percent, igic_types(id, value)),
        issues(id, issue_type_id, issue_sub_type_id, created_at, reason, is_opened)`
    }
  })

  const { data } = queryResult
  const order = data?.data

  const getTotalsReferral = async () => {
    const benefitData = await calculateReferralAndWalletAsync(order!.user)
    const { totalBenefits, totalWalletAmountUsed } = calculateReferralAndWalletAmounts(benefitData, order!.user)
    setWarningWalletAmountUsed(totalWalletAmountUsed > totalBenefits)
  }

  useEffect(() => {
    if (order) {
      getTotalsReferral()
    }
  }, [order])

  if (!order) return <></>

  order.user.orders = order.user.orders?.sort((a, b) => b.id - a.id)

  // Filter out deleted relations
  order.shipping_method.shipping_method_price_ranges =
    order?.shipping_method.shipping_method_price_ranges.filter(f => !f.deleted) || []

  order.supplements = order.supplements.filter(s => !s.deleted)

  order.refunds = order.refunds.filter(s => !s.deleted)

  order.items = order.items?.filter(i => !i.deleted)

  let supplementQuery: any = null
  let estimatedCostsQuery: any = null
  let refundQuery: any = null

  const setSuplementQuery = (value: any) => {
    if (supplementQuery === null) {
      supplementQuery = value
    }
  }

  const setEstimatedCostsQuery = (value: any) => {
    if (estimatedCostsQuery === null) {
      estimatedCostsQuery = value
    }
  }

  const setRefundQuery = (value: any) => {
    if (refundQuery === null) {
      refundQuery = value
    }
  }

  const refetchSup = () => {
    if (supplementQuery) supplementQuery.refetch()
  }
  const refetchRef = () => {
    if (refundQuery) refundQuery.refetch()
  }

  return (
    <>
      {warningWalletAmountUsed && (
        <Alert
          message={t('common.warning')}
          description={t('users.referral_benefits.warning_wallet_amount_used_desc')}
          type="warning"
          showIcon
          icon={<Icons.WarningOutlined />}
          style={{ marginBottom: 20 }}
        />
      )}
      <Row gutter={[15, 15]}>
        <Col xl={12} lg={24} xs={24}>
          <UserAddresses order={order} />
        </Col>

        <Col xl={12} xs={24}>
          <OrderDetails order={order} refetchFn={queryResult.refetch} />
        </Col>
      </Row>
      {order.status_id !== ORDER_STATUS_ID_SUBSCRIPTION && <>
        <Row gutter={[15, 15]} style={{ marginTop: 15 }}>
          <Col xl={24}>
            <OrderItems order={order} orderQuery={queryResult} refetchSup={() => refetchSup()} />
          </Col>
        </Row>
        <Row gutter={[15, 15]} style={{ marginTop: 15 }}>
          <Col xl={24}>
            <MarketOrders order={order} />
          </Col>
        </Row>
        <Row gutter={[15, 15]} style={{ marginTop: 15 }}>
          <Col xl={24}>
            <Supplements order={order} orderQuery={queryResult} setSupQuery={setSuplementQuery} refetchRef={() => refetchRef()} />
          </Col>
        </Row>
        <Row gutter={[15, 15]} style={{ marginTop: 15 }}>
          <Col xl={24}>
            <EstimatedCosts order={order} orderQuery={queryResult} setSupQuery={setEstimatedCostsQuery} />
          </Col>
        </Row>
        <Row gutter={[15, 15]} style={{ marginTop: 15 }}>
          <Col xl={24}>
            <Refunds order={order} orderQuery={queryResult} setRefQuery={setRefundQuery} />
          </Col>
        </Row>
      </>
      }
      {order.status_id === ORDER_STATUS_ID_SUBSCRIPTION && order.refunds.length > 0 &&
        <Row gutter={[15, 15]} style={{ marginTop: 15 }}>
          <Col xl={24}>
            <Refunds order={order} orderQuery={queryResult} setRefQuery={setRefundQuery} />
          </Col>
        </Row>
      }
    </>
  )
}
